import { useEffect, useRef, useCallback } from 'react';

/**
 * Resets a countdown timer on any user interaction (touch, mouse, keyboard).
 * Fires `onTimeout` when the user has been idle for `timeoutSec` seconds.
 */
export function useInactivityTimer(
  timeoutSec: number,
  onTimeout: () => void,
): void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(onTimeout);

  // Keep the callback reference fresh without re-attaching listeners.
  useEffect(() => {
    callbackRef.current = onTimeout;
  }, [onTimeout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      callbackRef.current();
    }, timeoutSec * 1000);
  }, [timeoutSec]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = [
      'touchstart',
      'mousedown',
      'mousemove',
      'keydown',
    ];

    // Start the initial timer immediately.
    resetTimer();

    const handler = () => resetTimer();

    for (const evt of events) {
      window.addEventListener(evt, handler, { passive: true });
    }

    return () => {
      for (const evt of events) {
        window.removeEventListener(evt, handler);
      }
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [resetTimer]);
}
